import { useEffect, useMemo, useState } from "react";
import { SearchInput, Tabs, TabsList, TabsTrigger } from "web-design-system";
import { getRouteApi } from "@tanstack/react-router";
import { Page } from "@core/components/layout/page";
import { FilterList } from "@core/components/filter-list";
import { NewsletterList } from "../components/newsletter-list";
import { KnowledgeNonNewsletterContent } from "../components/knowledge-non-newsletter-content";
import { useResourceTopicFilters } from "../hooks/use-resource-topic-filters";

type KnowledgeTab = "newsletter" | "article" | "tutorial" | "guide";
const routeApi = getRouteApi("/(auth)/_headed/resources/_layout/knowledge");

export default function KnowledgePage() {
  const navigate = routeApi.useNavigate();
  const { category } = routeApi.useSearch();
  const selectedTabFromUrl: KnowledgeTab = [
    "article",
    "tutorial",
    "guide",
  ].includes(category)
    ? category
    : "newsletter";
  const [selectedType, setSelectedType] =
    useState<KnowledgeTab>(selectedTabFromUrl);
  const [search, setSearch] = useState("");
  const isNewsletterTab = selectedType === "newsletter";
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const resourceTypeForFilters =
    selectedType === "article"
      ? "article"
      : selectedType === "tutorial"
        ? "tutorial"
        : "guide";

  const { filterOptions } = useResourceTopicFilters(resourceTypeForFilters);

  useEffect(() => {
    setSelectedType(selectedTabFromUrl);
  }, [selectedTabFromUrl]);

  const handleTypeChange = (type: KnowledgeTab) => {
    if (type === selectedType) return;

    setSelectedType(type);
    clearFilters();
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        category: type,
      }),
      replace: true,
    });
  };

  const clearFilters = () => {
    setSelectedFilters([]);
    setSearch("");
  };

  const toggleOption = (option: string) => {
    setSelectedFilters((current) =>
      current.includes(option)
        ? current.filter((value) => value !== option)
        : [...current, option]
    );
  };

  const filters = useMemo(() => {
    return [
      {
        title: "Topics",
        options: filterOptions.map((option) => ({
          id: option.key,
          label: option.label,
          count: option.count,
          checked: selectedFilters.includes(option.key),
          onCheckedChange: () => toggleOption(option.key),
        })),
      },
    ];
  }, [filterOptions, selectedFilters]);

  return (
    <>
      <Page.Header title="Knowledge Base" />

      <Page.Body className="overflow-hidden">
        <Tabs
          value={selectedType}
          onValueChange={(value) => handleTypeChange(value as KnowledgeTab)}
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden h-full"
        >
          <div className="flex shrink-0 gap-3 flex-row items-center justify-between">
            <TabsList className="h-auto w-fit justify-start rounded-lg bg-muted p-1">
              <TabsTrigger value="newsletter">Newsletter</TabsTrigger>
              <TabsTrigger value="article">Articles</TabsTrigger>
              <TabsTrigger value="tutorial">Tutorials</TabsTrigger>
              <TabsTrigger value="guide">Guides</TabsTrigger>
            </TabsList>

            {selectedType !== "newsletter" ? (
              <div className="w-[320px] pr-1 flex flex-row items-center gap-2">
                <SearchInput
                  value={search}
                  placeholder="Search for topics / keywords"
                  onChange={(event) => setSearch(event.target.value)}
                />
                {selectedType !== "guide" ? (
                  <div className="lg:hidden">
                    <FilterList
                      showAccordion={false}
                      filters={filters}
                      onClearAll={clearFilters}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          {isNewsletterTab ? (
            <NewsletterList key="newsletter" />
          ) : (
            <KnowledgeNonNewsletterContent
              key={selectedType}
              selectedType={selectedType}
              search={search}
              selectedFilters={selectedFilters}
              clearFilters={clearFilters}
              filters={filters}
            />
          )}
        </Tabs>
      </Page.Body>
    </>
  );
}
